import { useMemo, useState } from "react";
import { comics, comicCategories, ranking } from "@/mocks/comics";
import SectionHeading from "@/pages/home/components/SectionHeading";

const trendIcon: Record<string, { icon: string; className: string }> = {
  up: { icon: "ri-arrow-up-line", className: "text-accent-600" },
  down: { icon: "ri-arrow-down-line", className: "text-secondary-600" },
  flat: { icon: "ri-subtract-line", className: "text-foreground-400" },
};

export default function ComicLibrary() {
  const [category, setCategory] = useState("全部");
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return comics.filter((c) => {
      const matchCategory = category === "全部" || c.category === category;
      const matchQuery =
        q.length === 0 ||
        c.title.toLowerCase().includes(q) ||
        c.author.toLowerCase().includes(q) ||
        c.tags.some((t) => t.toLowerCase().includes(q));
      return matchCategory && matchQuery;
    });
  }, [category, query]);

  return (
    <section id="library" className="scroll-mt-20 bg-background-50 py-16 md:py-24">
      <div className="mx-auto max-w-7xl px-4 md:px-8">
        <SectionHeading
          eyebrow="在线漫画库"
          title="按分类挑作品，找到就能直接开读"
          desc="下面是漫画库中的部分热门作品，涵盖玄幻、都市、恋爱、科幻、武侠、悬疑、国风、热血与日常等方向。你可以切换分类或用关键词筛选，结果只作用于本页展示的作品。"
        />

        <div className="reveal mt-10 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-wrap gap-2" role="tablist" aria-label="漫画分类">
            {comicCategories.map((cat) => {
              const active = category === cat;
              return (
                <button
                  key={cat}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => setCategory(cat)}
                  className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm transition-colors ${
                    active
                      ? "bg-primary-500 text-background-50"
                      : "border border-background-200 bg-background-100 text-foreground-700 hover:border-primary-300 hover:text-primary-600"
                  }`}
                >
                  {cat}
                </button>
              );
            })}
          </div>

          <div className="relative w-full lg:w-72">
            <i
              className="ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-base text-foreground-400"
              aria-hidden="true"
            />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="搜索作品名 / 作者 / 标签"
              aria-label="搜索漫画作品"
              className="w-full rounded-md border border-background-200 bg-background-100 py-2.5 pl-10 pr-3 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:bg-background-50"
            />
          </div>
        </div>

        {filtered.length > 0 ? (
          <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
            {filtered.map((comic) => (
              <article
                key={comic.id}
                className="reveal group flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50"
              >
                <div className="relative aspect-[3/4] w-full overflow-hidden bg-background-200">
                  <img
                    src={comic.cover}
                    alt={`${comic.title} 漫画封面`}
                    title={`${comic.title} 包子漫画热门作品封面`}
                    className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  />
                  {comic.badge && (
                    <span className="absolute left-2 top-2 rounded-md bg-accent-500 px-2 py-0.5 text-[11px] font-medium text-background-50">
                      {comic.badge}
                    </span>
                  )}
                  <span className="absolute right-2 top-2 rounded-md bg-black/55 px-2 py-0.5 text-[11px] text-background-50">
                    {comic.status}
                  </span>
                </div>
                <div className="flex flex-1 flex-col p-3.5">
                  <h3 className="font-heading text-base text-foreground-950">{comic.title}</h3>
                  <p className="mt-1 text-xs text-foreground-500">
                    {comic.author} · {comic.category}
                  </p>
                  <div className="mt-2.5 flex flex-wrap gap-1.5">
                    {comic.tags.map((tag) => (
                      <span
                        key={tag}
                        className="rounded-full bg-secondary-100 px-2 py-0.5 text-[11px] text-secondary-900"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                  <p className="mt-3 line-clamp-2 text-xs leading-relaxed text-foreground-600">
                    {comic.description}
                  </p>
                  <div className="mt-auto pt-3">
                    <div className="flex items-center justify-between text-[11px] text-foreground-500">
                      <span className="inline-flex items-center gap-1">
                        <i className="ri-fire-line text-accent-500" aria-hidden="true" />
                        {comic.reads}
                      </span>
                      <span>{comic.latestChapter.split(" · ")[0]}</span>
                    </div>
                  </div>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <div className="reveal mt-8 rounded-lg border border-dashed border-background-300 bg-background-100 py-16 text-center">
            <i className="ri-emotion-sad-line text-3xl text-foreground-400" aria-hidden="true" />
            <p className="mt-3 text-sm text-foreground-600">没有找到符合条件的作品</p>
            <button
              type="button"
              onClick={() => {
                setCategory("全部");
                setQuery("");
              }}
              className="mt-4 whitespace-nowrap rounded-md border border-background-300 px-4 py-2 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
            >
              重置筛选条件
            </button>
          </div>
        )}

        <div className="reveal mt-14 rounded-lg border border-background-200 bg-background-100 p-6 md:p-8">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h3 className="flex items-center gap-2 font-heading text-lg text-foreground-950">
              <i className="ri-trophy-line text-xl text-secondary-600" aria-hidden="true" />
              本周人气榜单
            </h3>
            <span className="text-xs text-foreground-500">按近 7 日阅读量排序 · 数据每周更新</span>
          </div>
          <div className="mt-5 grid grid-cols-1 gap-3 md:grid-cols-2">
            {ranking.map((item) => {
              const trend = trendIcon[item.trend];
              return (
                <div
                  key={item.rank}
                  className="flex items-center gap-4 rounded-md border border-background-200 bg-background-50 px-4 py-3"
                >
                  <span className="w-6 shrink-0 font-heading text-lg text-primary-600">
                    {item.rank}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="truncate font-medium text-foreground-950">{item.title}</span>
                      <span className="shrink-0 rounded-full bg-background-200 px-2 py-0.5 text-[11px] text-foreground-600">
                        {item.category}
                      </span>
                    </div>
                    <p className="mt-0.5 text-xs text-foreground-500">作者：{item.author}</p>
                  </div>
                  <div className="shrink-0 text-right">
                    <div className="flex items-center justify-end gap-1 text-sm text-foreground-900">
                      <i className={trend.icon} aria-hidden="true" />
                      {item.reads}
                    </div>
                    <p className="text-[11px] text-foreground-400">近 7 日阅读</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}